function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { ClickAwayListener, Fade, IconButton } from "@mui/material";
import { useIntersectionObserver, useToggle } from "@react-hookz/web";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSdkContext } from "~/components";
import { theme, useResponsive } from "~/lib/media";
import { DashboardBadge } from "./badge";
import { usePushNotification } from "./display/notification-panel/hooks";
import { useRefreshSubscription } from "./display/notification-panel/notification-setting/hooks";
import { useReadingHistorySetup } from "./display/reading-history-panel/hooks";
import { useIsFirstTimeVisitDashboardWidget } from "./hooks";
import CloseIcon from "./icon-close.svg";
import LogoIcon from "./icon-logo.svg";
import { Container, DisplayDrawer, DisplayPopper, DrawerHeader, MenuContainer, StyledDashboardDisplay } from "./styles";
import { DashboardTooltips } from "./tooltips";
export const DashboardWidget = ({
  children,
  className,
  messageRenders,
  tooltipRenders
}) => {
  const {
    hasAcceptedGDPR,
    notification
  } = useSdkContext();
  useReadingHistorySetup();
  usePushNotification(notification.firebaseApp);
  useRefreshSubscription();
  const anchorElement = useRef(null);
  const [isTooltipVisible, setIsTooltipVisible] = useToggle(true);
  const [isOpen, setIsOpen] = useState(false);
  const observer = useIntersectionObserver(anchorElement);
  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const isDesktop = useResponsive(theme.breakpoints.up("desktop"), false);
  const renderDisplay = () => /*#__PURE__*/React.createElement(StyledDashboardDisplay, {
    beforeHeaderSlot: /*#__PURE__*/React.createElement(DrawerHeader, null, /*#__PURE__*/React.createElement(LogoIcon, null), /*#__PURE__*/React.createElement(IconButton, {
      "aria-label": "close",
      disableRipple: true,
      onClick: () => setIsOpen(false)
    }, /*#__PURE__*/React.createElement(CloseIcon, null))),
    messageRenders: messageRenders,
    "data-qa": "render-display"
  });
  const {
    setIsFirstTime
  } = useIsFirstTimeVisitDashboardWidget();
  useEffect(() => {
    if (!isOpen || !hasAcceptedGDPR) return;
    setIsFirstTime(false);
  }, [hasAcceptedGDPR, isOpen, setIsFirstTime]);
  useEffect(() => {
    setIsTooltipVisible(observer === null || observer === void 0 ? void 0 : observer.isIntersecting);
  }, [observer === null || observer === void 0 ? void 0 : observer.isIntersecting, setIsTooltipVisible]);
  const popperModifiers = useMemo(() => [{
    name: "offset",
    options: {
      // This is for handling tablet placement of the popper
      offset: [isDesktop ? 0 : 42, 10]
    }
  }], [isDesktop]);
  const scrollHandler = () => setIsOpen(false);
  useEffect(() => {
    if (isOpen && isDesktop) {
      window.addEventListener("scroll", scrollHandler);
    } else {
      return () => {
        window.removeEventListener("scroll", scrollHandler);
      };
    }
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [isDesktop, isOpen]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DashboardBadge, null, /*#__PURE__*/React.createElement(DashboardTooltips, {
    isDashboardOpen: isOpen,
    isTooltipVisible: isTooltipVisible,
    tooltipRenders: tooltipRenders
  }, /*#__PURE__*/React.createElement(Container, {
    className: className,
    ref: anchorElement,
    onClick: () => setIsOpen(true)
  }, children))), /*#__PURE__*/React.createElement(DisplayDrawer, {
    anchor: "bottom",
    open: isMobile && isOpen,
    SlideProps: {
      easing: {
        enter: "ease"
      },
      timeout: {
        enter: 500,
        exit: 0
      }
    },
    hideBackdrop: true,
    onClose: () => setIsOpen(false)
  }, renderDisplay()), /*#__PURE__*/React.createElement(DisplayPopper, {
    anchorEl: anchorElement.current,
    modifiers: popperModifiers,
    open: !isMobile && isOpen,
    placement: "bottom-end",
    transition: true
  }, ({
    TransitionProps
  }) => /*#__PURE__*/React.createElement(ClickAwayListener, {
    onClickAway: () => setIsOpen(false)
  }, /*#__PURE__*/React.createElement(Fade, _extends({}, TransitionProps, {
    timeout: {
      enter: 500,
      exit: 0
    }
  }), /*#__PURE__*/React.createElement(MenuContainer, null, renderDisplay())))));
};